/**
 * GaragePersistence - Tuning Garage Verwaltung & Punkte-System
 * Speichert Tuning-Fortschritt in separater DB-Tabelle
 * Verwaltet Spieler-Credits/Punkte für Tuning-Einkäufe
 */

import { CAR_STARTER_ID } from '../game/cars/CarCatalog';

export interface PlayerGarageData {
  userId: string;
  carId: string;
  installedParts: {
    engine?: string;
    suspension?: string;
    turbo?: string;
    nos?: string;
    bodykit?: string;
    wheels?: string;
    neon?: string;
    exhaust?: string;
  };
  visualCustomization: {
    primaryColor: string;
    secondaryColor: string;
    neonColor: string;
  };
  totalPointsEarned: number; // Gesamtpunkte je hochgerechnet
  pointsSpent: number; // Ausgegebene Punkte
  availableCredits: number; // Verfügbare Punkte zum Ausgeben
  lastQuizPointsSync: number; // Timestamp der letzten Sync
  createdAt: number;
  updatedAt: number;
}

export interface PlayerPointsSnapshot {
  userId: string;
  previousTotalPoints: number; // Der letzte bekannte Highscore
  currentTotalPoints: number; // Aktueller Highscore
  newPointsEarned: number; // Differenz = neu verdient
  earnedAt: number;
}

export class GaragePersistenceManager {
  private apiBaseUrl: string;

  constructor(baseUrl: string = '/api') {
    this.apiBaseUrl = baseUrl;
  }

  /**
   * Lade Garage-Daten für einen User
   * Falls nicht existiert, erstelle neue mit Stock-Auto
   */
  async loadGarageData(userId: string, token: string): Promise<PlayerGarageData> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/garage/${userId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 404) {
        // Keine Garage gefunden, erstelle neue
        return this.createDefaultGarage(userId);
      }

      if (!response.ok) {
        throw new Error(`Failed to load garage: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      console.error('[GaragePersistence] Error loading garage:', err);
      return this.createDefaultGarage(userId);
    }
  }

  /**
   * Synchronisiere Quiz-Punkte mit Garage
   * Berechne nur die Differenz seitdem letzter Sync
   */
  async syncPointsFromQuiz(
    userId: string,
    currentQuizTotalPoints: number,
    token: string
  ): Promise<{ pointsAdded: number; totalAvailable: number }> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/garage/${userId}/sync-points`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentQuizTotalPoints,
          timestamp: Date.now()
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to sync points: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        pointsAdded: result.pointsAdded || 0,
        totalAvailable: result.totalAvailable || 0
      };
    } catch (err) {
      console.error('[GaragePersistence] Error syncing points:', err);
      return { pointsAdded: 0, totalAvailable: 0 };
    }
  }

  /**
   * Kaufe und installiere ein Tuning-Teil
   * Zieht Punkte ab und speichert Änderung
   */
  async installTuningPart(
    userId: string,
    partId: string,
    partCost: number,
    category: string,
    token: string
  ): Promise<{ success: boolean; newBalance: number; message: string }> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/garage/${userId}/install-part`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          partId,
          category,
          partCost,
          timestamp: Date.now()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          newBalance: 0,
          message: error.message || 'Installation failed'
        };
      }

      const result = await response.json();
      return {
        success: true,
        newBalance: result.remainingCredits || 0,
        message: 'Installation successful!'
      };
    } catch (err) {
      console.error('[GaragePersistence] Error installing part:', err);
      return {
        success: false,
        newBalance: 0,
        message: 'Network error'
      };
    }
  }

  /**
   * Update visuelle Anpassungen (Farben, Neon)
   */
  async updateVisuals(
    userId: string,
    primaryColor: string,
    secondaryColor: string,
    neonColor: string,
    token: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/garage/${userId}/visuals`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          primaryColor,
          secondaryColor,
          neonColor,
          timestamp: Date.now()
        })
      });

      return response.ok;
    } catch (err) {
      console.error('[GaragePersistence] Error updating visuals:', err);
      return false;
    }
  }

  /**
   * Speichere Auto-Auswahl (welches Auto gerade aktiv ist)
   */
  async switchCar(userId: string, carId: string, token: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/garage/${userId}/switch-car`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          carId,
          timestamp: Date.now()
        })
      });

      return response.ok;
    } catch (err) {
      console.error('[GaragePersistence] Error switching car:', err);
      return false;
    }
  }

  /**
   * Helper: Erstelle Default-Garage
   */
  private createDefaultGarage(userId: string): PlayerGarageData {
    const now = Date.now();
    return {
      userId,
      carId: CAR_STARTER_ID,
      installedParts: {
        engine: 'engine_stock',
        suspension: 'suspension_stock',
        turbo: 'turbo_none',
        nos: 'nos_none',
        bodykit: 'bodykit_stock',
        wheels: 'wheels_stock',
        neon: 'neon_none',
        exhaust: 'exhaust_stock'
      },
      visualCustomization: {
        primaryColor: '#ff6600',
        secondaryColor: '#000000',
        neonColor: '#00ff00'
      },
      totalPointsEarned: 0,
      pointsSpent: 0,
      availableCredits: 0,
      lastQuizPointsSync: now,
      createdAt: now,
      updatedAt: now
    };
  }
}

/**
 * Hook für React: Manage Garage State
 */
export function useGaragePersistence(userId: string, token: string) {
  const manager = new GaragePersistenceManager('/api');

  const loadGarage = async () => {
    return await manager.loadGarageData(userId, token);
  };

  const syncPoints = async (quizPoints: number) => {
    return await manager.syncPointsFromQuiz(userId, quizPoints, token);
  };

  const buyPart = async (partId: string, cost: number, category: string) => {
    return await manager.installTuningPart(userId, partId, cost, category, token);
  };

  const updateColors = async (primary: string, secondary: string, neon: string) => {
    return await manager.updateVisuals(userId, primary, secondary, neon, token);
  };

  const selectCar = async (carId: string) => {
    return await manager.switchCar(userId, carId, token);
  };

  return {
    loadGarage,
    syncPoints,
    buyPart,
    updateColors,
    selectCar
  };
}
